/* ApiSource: the DataSource that talks to the server.
 *
 * THIS IS THE ONLY FILE IN apps/app THAT MAY CALL fetch. If a second one appears,
 * the seam is gone and with it the property the whole architecture buys: the demo
 * and the product being one codebase.
 *
 * ── THERE IS NO FALLBACK ─────────────────────────────────────────────────────
 * The previous build had one: a fixture behind this class, so a screen could be
 * built before the API answered. It went because of what it did when the paths were
 * wrong. getSession, login and logout asked for /api/session, which the server has
 * never served; every call 404'd, a 404 counted as "the server did not answer", and
 * the fixture answered instead. The app came up signed in as an invented coach,
 * showing invented numbers, on a box holding a real gym's data. The only sign was a
 * small pill.
 *
 * SITE-RULES 3 says never invent a number the product does not have. A fallback that
 * invents a whole gym whenever anything goes wrong is that rule's exact opposite, and
 * labelling it does not fix it: the label is the first thing a reader stops seeing.
 * When the server does not answer, the screen says the server did not answer.
 *
 * ── WHAT IS WIRED ────────────────────────────────────────────────────────────
 * The session and the roster, and nothing else yet. Every other method throws
 * NotWired, loudly and by name. That is deliberate: a stub returning an empty page
 * looks exactly like a gym with no leads, and the first person to see it would not
 * be me.
 */

import type {
  MacroTargets, Anchor, Campaign, CancellationResolution, EventKind, FollowUp, FollowUpStatus, GymClass, ISODate, Gym, Lead,
  LeadStage, Member, Permission, Role, Staff, TimelineEvent, User, UUID,
} from '@acrostic/model';
import type { MemberRecord } from '@acrostic/model/records';
import type { FirstNinety } from '@acrostic/model/track';
import type {
  Account, CampaignDraft, CampaignView, CellPeople, DashboardPayload, DataSource, DryRun,
  ApprovalQueue, ConversationThread, JourneyCell, LeadPage, LeadQuery, MemberQuery, NewLeadDraft, Page, Register, Schedule, SegmentView,
  StaffView, Worth, WorthSort,
} from './contract.js';

export interface ApiSourceOptions {
  /** Defaults to '/api'. The app is served from the same origin as the server. */
  base?: string;
}

/** The server could not be reached, or broke. Distinct from it answering "no". */
export class ApiUnavailable extends Error {}

/** The server answered 401. Not a failure: it is the answer "you are not signed in". */
export class Unauthenticated extends Error {}

/** Any other refusal the server made on purpose. It stands; nothing stands in for it. */
export class ApiError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}

/** A method this source has not been pointed at an endpoint yet. Named, so the
 *  screen says which one rather than showing an empty list. */
export class NotWired extends Error {
  constructor(what: string) {
    super(`${what} is not wired to the API yet. It works on the demo source.`);
  }
}

export class ApiSource implements DataSource {
  private readonly base: string;

  constructor(o: ApiSourceOptions = {}) {
    this.base = o.base ?? '/api';
  }

  /* ---- the one place a request is made ---- */

  private async req<T>(path: string, init?: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.base + path, {
        /* The session is an HttpOnly cookie the server sets. Same-origin, so the
           app has to be served from the same host as the API; there is no token in
           local storage for a script on the page to read. */
        credentials: 'same-origin',
        /* Spread the header in rather than setting it to undefined: under
           exactOptionalPropertyTypes an explicit undefined is not the same as an
           absent key, and RequestInit means the absent one. */
        ...(init?.body ? { headers: { 'content-type': 'application/json' } } : {}),
        ...init,
      });
    } catch (e) {
      throw new ApiUnavailable(`The server did not answer: ${(e as Error).message}`);
    }
    if (res.status === 204) return undefined as T;
    if (res.ok) return (await res.json()) as T;

    // The server's own words if it sent any; they are written to be read by a person.
    let said = '';
    try { said = ((await res.json()) as { error?: string }).error ?? ''; } catch { /* no body */ }

    if (res.status >= 500) throw new ApiUnavailable(said || `${res.status} from ${path}`);
    /* A 401 or a 404 IS the server answering. It is reachable; it said no. */
    if (res.status === 401) throw new Unauthenticated(said || 'Not signed in.');
    throw new ApiError(res.status, said || `${res.status} from ${path}`);
  }

  private static q(params: Record<string, string | number | undefined | null>): string {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null && v !== '') s.set(k, String(v));
    const out = s.toString();
    return out ? '?' + out : '';
  }

  /* ---- session ---- */

  async init(): Promise<void> { /* nothing to build: the server holds the gym. */ }

  async getSession(): Promise<{ user: User; gym: Gym; today: ISODate } | null> {
    // Not being signed in is the expected state of a fresh visit, not an error.
    try {
      return await this.req('/auth/session');
    } catch (e) {
      if (e instanceof Unauthenticated) return null;
      throw e;
    }
  }

  login(email: string, password: string): Promise<{ user: User; gym: Gym }> {
    return this.req('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
  }

  logout(): Promise<void> {
    return this.req('/auth/logout', { method: 'POST' });
  }

  /* ---- the roster ----
     Filtered, sorted and paged on the SERVER. There is no query string that returns
     the roster, which is the same rule SeedSource holds itself to.

     THE FIELD NAMES DIFFER BY TWO. The contract says coachId and supportCoachId; the
     endpoint takes coach and supportCoach. Mapped here rather than renamed at either
     end: the contract's names say what they hold, the endpoint's are already in a
     deployed API, and this is exactly the sort of translation a seam is for. */
  getMembers(q: MemberQuery = {}): Promise<Page<Member>> {
    return this.req('/members' + ApiSource.q({
      status: q.status, q: q.q, coach: q.coachId, supportCoach: q.supportCoachId,
      /* A boolean on a query string is a word, and `false` has to survive: `q()`
         drops undefined, so this is String()d rather than passed through. */
      ...(q.pt == null ? {} : { pt: String(q.pt) }),
      tier: q.tier, page: q.page, size: q.size, sort: q.sort,
    }));
  }

  /* THE ROW, NOT THE RECORD, and the difference is visible rather than silent.
   *
   * The contract says getMember returns a MemberRecord: the member with their
   * visits, scans and notes hanging off them. This endpoint returns the row, so the
   * three come back UNDEFINED - which is not the same as [] and must not be allowed
   * to look like it. An Attendance tab reading "No visits on record" on a gym with
   * nine thousand of them is a lie the screen has no way to know it is telling. The
   * tabs read undefined as "the server does not send this yet".
   *
   * Making the endpoint answer with the record is step C. */
  async getMember(id: UUID): Promise<MemberRecord | null> {
    try {
      return await this.req<MemberRecord>(`/members/${id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  }

  updateMember(id: UUID, patch: Partial<Member>): Promise<Member> {
    return this.req(`/members/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  getTimeline(id: UUID, o: { kinds?: EventKind[]; limit?: number } = {}): Promise<TimelineEvent[]> {
    return this.req(`/members/${id}/timeline` + ApiSource.q({ kinds: o.kinds?.join(','), limit: o.limit }));
  }

  addMemberNote(id: UUID, text: string): Promise<TimelineEvent> {
    return this.req(`/members/${id}/notes`, { method: 'POST', body: JSON.stringify({ text }) });
  }

  setMemberMacros(id: UUID, t: Omit<MacroTargets, 'setAt' | 'by'>): Promise<Member> {
    return this.req(`/members/${id}/macros`, { method: 'PUT', body: JSON.stringify(t) });
  }

  /* ---- cancellation initiated (PRD 4) ----
     Two routes because they are two different authorities, not one route with a
     flag: anybody may POST the first, and the server refuses the second to anyone
     who is not the Owner. A single /cancellation endpoint taking a state would
     have made "who may do this" a property of the body. */
  getConversation(id: UUID): Promise<ConversationThread> {
    return this.req(`/members/${id}/conversation`);
  }

  getFirstNinety(id: UUID): Promise<FirstNinety | null> {
    return this.req(`/members/${id}/first-ninety`);
  }

  recordCancellation(id: UUID, input: { asked: string }): Promise<Member> {
    return this.req(`/members/${id}/cancellation`, {
      method: 'POST', body: JSON.stringify(input),
    });
  }

  resolveCancellation(id: UUID, resolution: CancellationResolution): Promise<Member> {
    return this.req(`/members/${id}/cancellation/resolution`, {
      method: 'POST', body: JSON.stringify(resolution),
    });
  }

  /* ---- anchors (SITE-RULES 2) ----
     THE REFUSAL HAS TO LOOK THE SAME FROM BOTH SOURCES. The contract says null means
     "that is not an anchor", which is what makeAnchor returns in the model and what
     SeedSource passes on. The server says the same thing with a 422 and
     `reason: no_basis`. Translated here, because a screen that had to tell a null
     from a 422 would be a screen that knows which source answered, and then the seam
     is decoration.

     Any OTHER refusal still stands as an error: this maps one specific, documented
     no, not every unhappy answer. */
  async addMemberAnchor(id: UUID, a: Omit<Anchor, 'id' | 'lastTwoWay'>): Promise<Anchor | null> {
    try {
      return await this.req<Anchor>(`/members/${id}/anchors`, { method: 'POST', body: JSON.stringify(a) });
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) return null;
      throw e;
    }
  }
  touchMemberAnchor(id: UUID, name: string): Promise<Anchor | null> {
    return this.req(`/members/${id}/anchors/${encodeURIComponent(name)}/spoke`, { method: 'POST' });
  }
  removeMemberAnchor(id: UUID, name: string): Promise<void> {
    return this.req(`/members/${id}/anchors/${encodeURIComponent(name)}`, { method: 'DELETE' });
  }

  /* ---- leads ----
     The endpoint predates the dropout reason, so a lead comes back without one and
     it is defaulted to null HERE rather than left off the type. Null is the honest
     answer: this server does not record one yet. */
  /* SCOPE AND OWNER ARE NOT SENT, AND THAT IS A REFUSAL RATHER THAN AN OMISSION.
     The route takes `stage`, and its no-stage default is exactly scope 'open', so
     'open' is honoured by asking for nothing. 'closed' and 'all' are not one stage
     and the route cannot express them; `ownerId` it does not filter on at all.
     Dropping either QUIETLY is how a seam drifts - the seeded source would filter,
     the server would not, and the screen would be right in the demo and wrong in
     production. Nobody asks for these today; whoever does gets told, here, rather
     than getting the whole pipeline back and believing it. */
  async getLeads(q: LeadQuery = {}): Promise<LeadPage> {
    if (q.scope && q.scope !== 'open') {
      throw new Error(`getLeads: the API serves the open pipeline only, not scope '${q.scope}'.`);
    }
    if (q.ownerId) throw new Error('getLeads: the API does not filter by ownerId yet.');
    const page = await this.req<LeadPage>('/leads' + ApiSource.q({
      stage: q.stage, q: q.q, page: q.page, size: q.size,
    }));
    return { ...page, items: page.items.map(withNoLostReason) };
  }

  async getLead(id: UUID): Promise<Lead | null> {
    try {
      return withNoLostReason(await this.req<Lead>(`/leads/${id}`));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  }

  async updateLead(id: UUID, patch: Partial<Lead>): Promise<Lead> {
    return withNoLostReason(await this.req<Lead>(`/leads/${id}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }));
  }

  /* ONE PERSON, ONE JOURNEY. The lead half reaches every stage, won and lost included:
     the board shows only the open pipeline, so this is the way back to somebody who
     joined or said no. */
  async searchPeople(q: string): Promise<{ members: Member[]; leads: Lead[] }> {
    const r = await this.req<{ members: Member[]; leads: Lead[] }>(
      '/search' + ApiSource.q({ q }));
    return { members: r.members, leads: r.leads.map(withNoLostReason) };
  }

  /* Not paged: a gym has tens of staff, not thousands, and every picker that offers
     "assign to" needs the whole list to offer. */
  async getStaff(): Promise<StaffView[]> {
    return this.req('/staff');
  }

  async createLead(draft: NewLeadDraft): Promise<Lead> {
    return withNoLostReason(await this.req<Lead>('/leads', {
      method: 'POST', body: JSON.stringify(draft),
    }));
  }

  async addLeadNote(id: UUID, text: string): Promise<TimelineEvent> {
    return this.req(`/leads/${id}/notes`, { method: 'POST', body: JSON.stringify({ text }) });
  }

  /* A STAGE MOVE IS ITS OWN ENDPOINT because closing a lead as lost carries a rule a
     column write does not: a reason, or it does not happen. The server refuses with
     422, and the refusal is rethrown as a plain Error so the screen shows the sentence
     the server sent rather than a status code. */
  async setLeadStage(
    id: UUID, stage: LeadStage, lost?: { reason: string; detail: string },
  ): Promise<Lead> {
    try {
      return withNoLostReason(await this.req<Lead>(`/leads/${id}/stage`, {
        method: 'PUT', body: JSON.stringify({ stage, ...(lost ? { lost } : {}) }),
      }));
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) throw new Error(e.message);
      throw e;
    }
  }

  async bookTrial(id: UUID, classId: UUID, date: ISODate): Promise<Lead> {
    return withNoLostReason(await this.req<Lead>(`/leads/${id}/trial`, {
      method: 'POST', body: JSON.stringify({ classId, date }),
    }));
  }

  /* ---- classes ---- */
  getClasses(date: ISODate): Promise<GymClass[]> {
    return this.req('/classes' + ApiSource.q({ date }));
  }
  checkIn(memberId: UUID, classId: UUID, date: ISODate): Promise<void> {
    return this.req(`/classes/${classId}/check-in`, {
      method: 'POST', body: JSON.stringify({ memberId, date }),
    });
  }
  undoCheckIn(memberId: UUID, classId: UUID, date: ISODate): Promise<void> {
    return this.req(`/classes/${classId}/check-in`, {
      method: 'DELETE', body: JSON.stringify({ memberId, date }),
    });
  }
  /* THE DAY, in one call. The demo built this in the screen - it walked the roster and
     all of their visits for every session, twice - and at 3,000 members that was 59%
     of the render. The server holds the history, so the server answers it. */
  getSchedule(date: ISODate): Promise<Schedule> {
    return this.req('/classes/schedule' + ApiSource.q({ date }));
  }
  getRegister(classId: UUID, date: ISODate): Promise<Register> {
    return this.req(`/classes/${classId}/register` + ApiSource.q({ date }));
  }

  /* ---- follow-ups (SITE-RULES 11d) ----
     The loop is only closed by an outcome, and it is a PATCH because recording one
     is an edit to the follow-up rather than a new thing. */
  getFollowUps(q: { ownerId?: UUID; status?: FollowUpStatus } = {}): Promise<FollowUp[]> {
    return this.req('/follow-ups' + ApiSource.q({ owner: q.ownerId, status: q.status }));
  }
  recordOutcome(id: UUID, outcome: string, detail: string): Promise<FollowUp> {
    return this.req(`/follow-ups/${id}`, {
      method: 'PATCH', body: JSON.stringify({ outcome, outcomeDetail: detail, status: 'completed' }),
    });
  }

  /* ---- approvals ----
     Approving APPLIES the change to the record it names; the server does that, and
     this only asks for it. */
  getApprovals(): Promise<ApprovalQueue> {
    return this.req('/approvals');
  }
  decideApproval(id: UUID, decision: 'approved' | 'rejected'): Promise<void> {
    return this.req(`/approvals/${id}/decide`, {
      method: 'POST', body: JSON.stringify({ decision }),
    });
  }

  /* ---- the console ----
     One aggregate call. It never ships the roster: what comes back is eight to
     twelve named people and a handful of numbers derived from the rest.

     THE OPTIONAL FIELDS ARE THE REWRITE'S. The same endpoint serves the v2 app,
     which reads `stats.at_risk` and friends and is live on two real gyms, so the
     new fields were added beside the old ones rather than replacing them. A server
     that has not been updated yet answers without them, and the defaults below are
     what an empty console looks like - not invented numbers, just nothing to show. */
  async getDashboard(seat: Role): Promise<DashboardPayload> {
    const d = await this.req<Partial<DashboardPayload> & {
      stats: Record<string, number>; goingQuiet: Member[]; followUps: FollowUp[];
      journey: JourneyCell[]; riskModel: DashboardPayload['riskModel']; suppressedCount: number;
    }>('/dashboard' + ApiSource.q({ seat }));
    return {
      ...d,
      /* An empty list, not an invented one. A server that has not shipped the state
         yet says nothing about it, and the panel then renders nothing rather than
         claiming no member of this gym has ever asked to cancel. */
      cancellations: d.cancellations ?? [],
      unanchored: d.unanchored ?? [],
      careReviews: d.careReviews ?? [],
      coverage: d.coverage ?? { covered: 0, of: 0 },
      tiers: d.tiers ?? { Red: 0, Amber: 0, Green: 0 },
      onboardingCount: d.onboardingCount ?? 0,
      scoped: d.scoped ?? false,
      bookSize: d.bookSize ?? d.stats['members'] ?? 0,
    };
  }

  getJourney(): Promise<JourneyCell[]> {
    return this.req('/journey');
  }

  /* ---- everything else, by name, until it is wired ---- */




  /* Context nobody writes down is context that dies in the room. A follow-up is a
     chase and the server refuses it on a suppressed member (SITE-RULES 1). */
  async addRegisterNote(
    memberId: UUID, classId: UUID, text: string, kind: 'note' | 'followup',
  ): Promise<void> {
    await this.refusable(() => this.req(`/classes/${classId}/notes`, {
      method: 'POST', body: JSON.stringify({ memberId, text, kind }),
    }));
  }


  /* SITE-RULES 11e, end to end: the count opens its names, and the names come from the
     SAME lifecycleCellPeople() call on the server that produced the count. Both halves
     are paged because `member` is the whole roster. */
  async getJourneyCellPeople(
    key: string, q: { page?: number; size?: number } = {},
  ): Promise<CellPeople> {
    return this.req(`/journey/${encodeURIComponent(key)}/people`
      + ApiSource.q({ page: q.page, size: q.size }));
  }

  /* WORTH AGAINST VALUE, two columns on purpose: blending what somebody pays with what
     they bring into one score would be inventing a number. Owner only, and the server
     says so with 403 rather than answering with a page of nulls. */
  getWorth(q: { page?: number; size?: number; sort?: WorthSort } = {}): Promise<Page<Worth>> {
    return this.req('/worth' + ApiSource.q({ page: q.page, size: q.size, sort: q.sort }));
  }
  /* Every refusal below - nobody locks themselves out, a gym keeps one Owner, a seat
     you cannot grant is one you cannot take away - is answered by the server with 422
     and rethrown as a plain Error, so the screen shows the sentence it sent. The flags
     on Account are so the screen does not OFFER what is about to be refused. */
  getAccounts(): Promise<Account[]> {
    return this.req('/accounts');
  }
  async createAccount(
    d: { name: string; email: string; roles: Role[] },
  ): Promise<{ account: Account; password: string }> {
    return this.refusable(() => this.req('/accounts', {
      method: 'POST', body: JSON.stringify(d),
    }));
  }
  async setAccountRoles(id: UUID, roles: Role[]): Promise<Account> {
    return this.refusable(() => this.req(`/accounts/${id}/roles`, {
      method: 'PUT', body: JSON.stringify({ roles }),
    }));
  }
  async setAccountPermissions(id: UUID, permissions: Permission[] | null): Promise<Account> {
    return this.refusable(() => this.req(`/accounts/${id}/permissions`, {
      method: 'PUT', body: JSON.stringify({ permissions }),
    }));
  }
  async resetAccountPassword(id: UUID): Promise<string> {
    const r = await this.refusable(
      () => this.req<{ password: string }>(`/accounts/${id}/password`, { method: 'POST' }));
    return r.password;
  }
  async removeAccount(id: UUID): Promise<void> {
    await this.refusable(() => this.req(`/accounts/${id}`, { method: 'DELETE' }));
  }

  async updateSettings(patch: Record<string, unknown>): Promise<Gym> {
    return this.refusable(() => this.req<Gym>('/settings', {
      method: 'PATCH', body: JSON.stringify(patch),
    }));
  }

  /* A RULE REFUSED IS NOT A FAULT. The server answers 422 with the sentence the screen
     should show; anything else is a real error and travels as one. */
  private async refusable<T>(run: () => Promise<T>): Promise<T> {
    try {
      return await run();
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) throw new Error(e.message);
      throw e;
    }
  }

  /* SEGMENTS ARE A QUESTION ASKED OF THE ROSTER, answered by the same SEGMENTS rules
     and segmentMembers() the seed uses - on the server, because the server holds the
     roster. Nothing about a segment is stored on either side. */
  getSegments(): Promise<SegmentView[]> {
    return this.req('/segments');
  }
  getSegmentMembers(id: string, q: { page?: number; size?: number } = {}): Promise<Page<Member>> {
    return this.req(`/segments/${encodeURIComponent(id)}/members`
      + ApiSource.q({ page: q.page, size: q.size }));
  }
  /* NOTHING HERE SENDS. Sending is switched on by Acrostic once a domain is verified
     and a number is provisioned; until then a campaign is written, previewed and goes
     nowhere, and the counters say so. */
  getCampaigns(): Promise<CampaignView[]> {
    return this.req('/campaigns');
  }
  /* A campaign pointed at the Care Track list is refused with 422 (SITE-RULES 1), and
     the refusal is rethrown as a plain Error so the screen shows the server's sentence
     rather than a status code. */
  async saveCampaign(d: CampaignDraft): Promise<Campaign> {
    try {
      return await this.req<Campaign>('/campaigns', {
        method: 'POST', body: JSON.stringify(d),
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) throw new Error(e.message);
      throw e;
    }
  }
  async deleteCampaign(id: UUID): Promise<void> {
    await this.req(`/campaigns/${id}`, { method: 'DELETE' });
  }
  dryRunCampaign(id: UUID): Promise<DryRun> {
    return this.req(`/campaigns/${id}/dry-run`);
  }

}

/* The Staff type is referenced by StaffView, which is referenced above. Imported so
   the file states its whole dependency surface rather than reaching through one. */
export type { Staff };

/* The endpoint predates lostReason. Defaulted to null rather than left off the type:
   null is what this server knows, and a Lead without the field would not satisfy the
   contract every other source does. */
function withNoLostReason(l: Lead): Lead {
  /* Spread FIRST, then fill. The other way round, a payload carrying the key with
     an undefined value overwrites the default with undefined, and the field is then
     absent on a type that says it cannot be. */
  return { ...l, lostReason: l.lostReason ?? null, lostReasonDetail: l.lostReasonDetail ?? null };
}
